/* global axios */
import ApiClient from './ApiClient';
import store from '../store';
import webhookService from '../services/kanban/webhookService';

class KanbanAPI extends ApiClient {
  constructor() {
    super('kanban_items', { accountScoped: true });
  }

  get accountId() {
    return store.state.auth.currentUser.account_id;
  }

  async getItems(
    funnelId,
    page = 1,
    stageId = null,
    agentId = null,
    conversationDisplayId = null,
    { hasConversation = false } = {}
  ) {
    const selectedFunnel = store.getters['funnel/getSelectedFunnel'];
    const finalFunnelId = funnelId || selectedFunnel?.id;


    if (!finalFunnelId) {
      throw new Error('No funnel selected or provided');
    }

    const params = {
      funnel_id: finalFunnelId,
      stage_id: stageId, // Sempre incluir stage_id para evitar itens ocultos
      page: page > 1 ? page : undefined,
      agent_id: agentId || undefined,
      conversation: conversationDisplayId || undefined,
      has_conversation: hasConversation ? true : undefined,
    };

    // Remover parâmetros undefined
    Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);


    // Usar axios diretamente para garantir que os parâmetros sejam enviados
    return axios.get(this.url, { params });
  }

  async getItemsByConversation(conversationDisplayId, page = 1) {
    if (!conversationDisplayId) return { data: { items: [], pagination: {} } };
    const params = { conversation: conversationDisplayId, page: page > 1 ? page : undefined };
    Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);
    return axios.get(this.url, { params });
  }

  async getItem(itemId) {
    const response = await axios.get(`${this.url}/${itemId}`);
    return response;
  }

  async createItem(data) {
    try {
      const response = await axios.post(this.url, {
        kanban_item: data,
      });
      await webhookService.notifyItemCreated(response.data);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async updateItem(itemId, data) {
    try {
      const { data: currentItem } = await this.getItem(itemId);
      const response = await axios.put(`${this.url}/${itemId}`, {
        kanban_item: data,
      });

      const changes = {};
      Object.keys(data).forEach(key => {
        if (JSON.stringify(currentItem[key]) !== JSON.stringify(data[key])) {
          changes[key] = {
            from: currentItem[key],
            to: data[key],
          };
        }
      });

      if (Object.keys(changes).length > 0) {
        await webhookService.notifyItemUpdated(response.data, changes);
      }

      return response;
    } catch (error) {
      throw error;
    }
  }

  async deleteItem(itemId) {
    try {
      const { data: currentItem } = await this.getItem(itemId);
      const response = await axios.delete(`${this.url}/${itemId}`);

      await webhookService.notifyItemDeleted(currentItem);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async moveToStage(itemId, funnelStage, funnelId = null) {
    try {

      const { data: currentItem } = await this.getItem(itemId);
      const fromStage = currentItem.funnel_stage;


      // Monta o payload dinamicamente
      const payload = {
        funnel_stage: funnelStage,
        stage_entered_at: new Date().toISOString(),
      };
      if (funnelId) {
        payload.funnel_id = funnelId;
      }


      const moveResponse = await axios.post(
        `${this.url}/${itemId}/move_to_stage`,
        payload
      );


      const { data: updatedItem } = await this.getItem(itemId);


      if (updatedItem) {
        await webhookService.notifyStageChange(
          updatedItem,
          fromStage,
          funnelStage
        );

        return updatedItem;
      } else {
        return null;
      }
    } catch (error) {
      throw error;
    }
  }

  async moveToFunnel(itemId, funnelId, funnelStage = null) {
    try {
      const { data: currentItem } = await this.getItem(itemId);
      const oldFunnelId = currentItem.funnel_id;
      const oldStage = currentItem.funnel_stage;

      const payload = {
        funnel_id: funnelId,
      };
      if (funnelStage) {
        payload.funnel_stage = funnelStage;
        payload.stage_entered_at = new Date().toISOString();
      }

      const response = await axios.post(`${this.url}/${itemId}/move`, payload);

      const { data: updatedItem } = await this.getItem(itemId);

      if (updatedItem) {
        // Notificar mudança de funil ou estágio
        const changes = {};
        if (oldFunnelId !== funnelId) {
          changes.funnel_id = { from: oldFunnelId, to: funnelId };
        }
        if (funnelStage && oldStage !== funnelStage) {
          changes.funnel_stage = { from: oldStage, to: funnelStage };
          await webhookService.notifyStageChange(
            updatedItem,
            oldStage,
            funnelStage
          );
        }

        if (Object.keys(changes).length > 0) {
          await webhookService.notifyItemUpdated(updatedItem, changes);
        }

        return updatedItem;
      } else {
        return null;
      }
    } catch (error) {
      throw error;
    }
  }

  async reorderItems(positions) {
    try {
      const currentItems = await Promise.all(
        positions.map(pos => this.getItem(pos.id))
      );

      const response = await axios.post(`${this.url}/reorder`, {
        positions,
      });

      const changes = positions.map(pos => ({
        item_id: pos.id,
        old_position: currentItems.find(item => item.data.id === pos.id)?.data
          .position,
        new_position: pos.position,
      }));

      await webhookService.notifyItemReordered(response.data, changes);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async startTimer(itemId) {
    try {
      const response = await this.updateItem(itemId, {
        timer_started_at: new Date().toISOString(),
      });

      await webhookService.notifyTimerStarted(response.data);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async stopTimer(itemId) {
    try {
      const response = await this.updateItem(itemId, {
        timer_started_at: null,
      });

      const duration = response.data.timer_duration;
      await webhookService.notifyTimerStopped(response.data, duration);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getItemByConversationId(conversationId) {
    return this.getItemsByConversation(conversationId);
  }

  // Métodos para gerenciar agentes atribuídos
  async assignAgent(itemId, agentId) {
    try {
      const response = await axios.post(`${this.url}/${itemId}/assign_agent`, {
        agent_id: agentId,
      });

      // Atualizar o store com os novos dados
      store.dispatch('kanban/updateItemDetails', {
        itemId,
        details: response.data
      });

      return response;
    } catch (error) {
      throw error;
    }
  }

  async removeAgent(itemId, agentId) {
    try {
      const response = await axios.delete(`${this.url}/${itemId}/remove_agent`, {
        data: {
          agent_id: agentId,
        },
      });

      // Atualizar o store com os novos dados
      store.dispatch('kanban/updateItemDetails', {
        itemId,
        details: response.data
      });

      return response;
    } catch (error) {
      throw error;
    }
  }

  async getAssignedAgents(itemId) {
    try {
      const response = await axios.get(`${this.url}/${itemId}/assigned_agents`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getBatchData(funnelId) {
    const response = await axios.get(`${this.url}/batch`, {
      params: {
        funnel_id: funnelId,
      },
    });

    return response.data;
  }

  async searchItems(query, funnelId = null, agentId = null) {
    try {
      const selectedFunnel = store.getters['funnel/getSelectedFunnel'];
      const finalFunnelId = funnelId || selectedFunnel?.id;

      if (!finalFunnelId) {
        throw new Error('No funnel selected or provided');
      }

      const params = {
        query: query,
        funnel_id: finalFunnelId,
        agent_id: agentId || undefined,
      };

      // Remover parâmetros undefined
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      const response = await axios.get(`${this.url}/search`, { params });
      return response;
    } catch (error) {
      throw error;
    }
  }

  async filterItems(filters = {}, funnelId = null) {
    try {
      const selectedFunnel = store.getters['funnel/getSelectedFunnel'];
      const finalFunnelId = funnelId || selectedFunnel?.id;

      if (!finalFunnelId) {
        throw new Error('No funnel selected or provided');
      }

      const params = {
        funnel_id: finalFunnelId,
        priorities: filters.priorities || undefined,
        value_min: filters.valueMin || undefined,
        value_max: filters.valueMax || undefined,
        agent_id: filters.agent || undefined,
        conversation: filters.conversationDisplayId || undefined,
        date_start: filters.dateStart || undefined,
        date_end: filters.dateEnd || undefined,
        scheduled_date_start: filters.scheduledDateStart || undefined,
        scheduled_date_end: filters.scheduledDateEnd || undefined,
      };

      // Remover parâmetros undefined ou arrays vazios
      Object.keys(params).forEach(key => {
        if (params[key] === undefined || (Array.isArray(params[key]) && params[key].length === 0)) {
          delete params[key];
        }
      });

      const response = await axios.get(`${this.url}/filter`, { params });
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getReports(params = {}) {
    try {
      const requestParams = {
        funnel_id: params.funnel_id,
        from: params.from,
        to: params.to,
        user_ids: params.user_ids,
        inbox_id: params.inbox_id,
        channel: params.channel,
      };
      Object.keys(requestParams).forEach(key => {
        const v = requestParams[key];
        if (v === undefined || v === null || v === '') {
          delete requestParams[key];
        }
      });
      const response = await axios.get(`${this.url}/reports`, {
        params: requestParams,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para atribuir agente a um item específico do checklist
  async assignAgentToChecklistItem(itemId, checklistItemId, agentId) {
    try {
      const response = await axios.post(`${this.url}/${itemId}/assign_agent_to_checklist_item`, {
        checklist_item_id: checklistItemId,
        agent_id: agentId,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para remover agente de um item específico do checklist
  async removeAgentFromChecklistItem(itemId, checklistItemId) {
    try {
      const response = await axios.delete(`${this.url}/${itemId}/remove_agent_from_checklist_item`, {
        data: {
          checklist_item_id: checklistItemId,
        },
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para criar checklist item
  async createChecklistItem(itemId, checklistData) {
    try {
      const response = await axios.post(`${this.url}/${itemId}/create_checklist_item`, {
        text: checklistData.text,
        attachments: checklistData.attachments || [],
        linked_item_id: checklistData.linked_item_id,
        linked_conversation_id: checklistData.linked_conversation_id,
        linked_contact_id: checklistData.linked_contact_id,
        due_date: checklistData.due_date,
        priority: checklistData.priority || 'none',
        agent_id: checklistData.agent_id,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para atualizar checklist item
  async updateChecklistItem(itemId, checklistItemId, updateData) {
    try {
      const response = await axios.patch(`${this.url}/${itemId}/update_checklist_item`, {
        checklist_item_id: checklistItemId,
        ...updateData
      });

      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para marcar/desmarcar item do checklist
  async toggleChecklistItem(itemId, checklistItemId) {
    try {
      const response = await axios.post(`${this.url}/${itemId}/toggle_checklist_item`, {
        checklist_item_id: checklistItemId,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para deletar checklist item
  async deleteChecklistItem(itemId, checklistItemId) {
    try {
      const response = await axios.delete(`${this.url}/${itemId}/delete_checklist_item`, {
        data: {
          checklist_item_id: checklistItemId
        }
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para criar nota
  async createNote(itemId, noteData) {
    try {
      const response = await axios.post(`${this.url}/${itemId}/create_note`, {
        text: noteData.text,
        attachments: noteData.attachments || [],
        linked_item_id: noteData.linked_item_id,
        linked_conversation_id: noteData.linked_conversation_id,
        linked_contact_id: noteData.linked_contact_id,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para atualizar nota
  async updateNote(itemId, noteId, noteData) {
    try {
      const response = await axios.patch(`${this.url}/${itemId}/update_note`, {
        note_id: noteId,
        text: noteData.text,
        attachments: noteData.attachments || [],
        linked_item_id: noteData.linked_item_id,
        linked_conversation_id: noteData.linked_conversation_id,
        linked_contact_id: noteData.linked_contact_id,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para buscar notas do item
  async getNotes(itemId) {
    try {
      const response = await axios.get(`${this.url}/${itemId}/get_notes`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para buscar checklist do item
  async getChecklists(itemId) {
    try {
      const response = await axios.get(`${this.url}/${itemId}/get_checklist`);
      return response;
    } catch (error) {
      throw error;
    }
  }


  // Método para deletar nota
  async deleteNote(itemId, noteId) {
    try {
      const response = await axios.delete(`${this.url}/${itemId}/delete_note`, {
        data: {
          note_id: noteId
        }
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para fazer upload de anexo do item kanban
  async uploadAttachment(itemId, file) {
    try {
      const formData = new FormData();
      formData.append('attachment', file);

      // Usar a URL correta para attachments: kanban/items ao invés de kanban_items
      const attachmentUrl = `${this.baseUrl()}/kanban/items/${itemId}/attachments`;

      const response = await axios.post(attachmentUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para deletar anexo do item kanban
  async deleteAttachment(itemId, attachmentId) {
    try {
      // Usar a URL correta para attachments: kanban/items ao invés de kanban_items
      const attachmentUrl = `${this.baseUrl()}/kanban/items/${itemId}/attachments/${attachmentId}`;

      const response = await axios.delete(attachmentUrl);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para obter todos os anexos de um item kanban
  async getAttachments(itemId) {
    try {
      // Usar a URL correta para attachments: kanban/items ao invés de kanban_items
      const attachmentUrl = `${this.baseUrl()}/kanban/items/${itemId}/attachments`;

      const response = await axios.get(attachmentUrl);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para obter relatório de tempo do item
  async getTimeReport(itemId) {
    try {
      const response = await axios.get(`${this.url}/${itemId}/time_report`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para obter detalhamento de tempo por etapa
  async getStageTimeBreakdown(itemId) {
    try {
      const response = await axios.get(`${this.url}/${itemId}/stage_time_breakdown`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para duplicar checklist para outro item
  async duplicateChecklist(sourceItemId, targetItemId, options = {}) {
    try {
      const response = await axios.post(`${this.url}/${sourceItemId}/duplicate_checklist`, {
        target_item_id: targetItemId,
        merge: options.merge || false
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para buscar itens no checklist
  async searchChecklist(itemId, query = '') {
    try {
      const params = query ? { query } : {};
      const response = await axios.get(`${this.url}/${itemId}/search_checklist`, { params });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para obter progresso do checklist por agente
  async getChecklistProgressByAgent(itemId) {
    try {
      const response = await axios.get(`${this.url}/${itemId}/checklist_progress_by_agent`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para obter counts de notas, checklist e anexos
  async getCounts(itemId) {
    try {
      const response = await axios.get(`${this.url}/${itemId}/counts`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // AÇÕES EM MASSA

  // Método para mover múltiplos itens para uma nova etapa
  async bulkMoveItems(itemIds, newStage, funnelId = null) {
    try {
      const payload = {
        item_ids: itemIds,
        new_stage: newStage
      };

      if (funnelId) {
        payload.funnel_id = funnelId;
      }

      const response = await axios.post(`${this.url}/bulk_move_items`, payload);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para atribuir agente para múltiplos itens
  async bulkAssignAgent(itemIds, agentId, mode = 'replace') {
    try {
      const response = await axios.post(`${this.url}/bulk_assign_agent`, {
        item_ids: itemIds,
        agent_id: agentId,
        mode: mode // 'replace' ou 'add'
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para definir prioridade para múltiplos itens
  async bulkSetPriority(itemIds, priority) {
    try {
      const response = await axios.post(`${this.url}/bulk_set_priority`, {
        item_ids: itemIds,
        priority: priority
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para mudar o status do item
  async changeStatus(itemId, status) {
    try {
      const response = await axios.post(`${this.url}/${itemId}/change_status`, {
        status: status
      });

      await webhookService.notifyItemUpdated(response.data, {
        status: { from: null, to: status }
      });

      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para exportar itens do funil em CSV
  async exportItems(funnelId, filters = {}) {
    try {
      const params = {
        funnel_id: funnelId,
        priorities: filters.priorities || undefined,
        value_min: filters.valueMin || undefined,
        value_max: filters.valueMax || undefined,
        agent_id: filters.agent || undefined,
        date_start: filters.dateStart || undefined,
        date_end: filters.dateEnd || undefined,
        scheduled_date_start: filters.scheduledDateStart || undefined,
        scheduled_date_end: filters.scheduledDateEnd || undefined,
      };

      // Remover parâmetros undefined ou arrays vazios
      Object.keys(params).forEach(key => {
        if (params[key] === undefined || (Array.isArray(params[key]) && params[key].length === 0)) {
          delete params[key];
        }
      });

      const response = await axios.get(`${this.url}/export`, {
        params,
        responseType: 'blob'
      });

      // Criar link para download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Extrair nome do arquivo do header Content-Disposition ou usar padrão
      const contentDisposition = response.headers['content-disposition'];
      let filename = `kanban_items_${funnelId}_${new Date().getTime()}.csv`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para fazer preview do CSV antes da importação
  async importPreview(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${this.url}/import_preview`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response;
    } catch (error) {
      throw error;
    }
  }

  // Método para importar itens do CSV
  async importItems(funnelId, file, mappings, defaultStageId = null) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('funnel_id', funnelId);
      formData.append('mappings', JSON.stringify(mappings));
      if (defaultStageId) {
        formData.append('default_stage_id', defaultStageId);
      }

      const response = await axios.post(`${this.url}/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response;
    } catch (error) {
      throw error;
    }
  }
}

export default new KanbanAPI();
